// The workspace layout roster, the ONE rule for "a panel destination surfaces
// the panel" (th-hk9nl), the ONE rule for which layouts a viewport can fit
// (`LAYOUT_MIN_VIEWPORT_HEIGHT`, th-5n61wn), and the ONE layout-hold shape a
// surface that takes the panel records and puts back (`captureLayoutHold`).
//
// `fullScreen` draws no secondary panel. A destination that names a panel tab,
// reached while the workspace is `fullScreen`, lands on a panel nobody can see,
// so the layout leaves `fullScreen` for `sideBySide`. A layout that already
// draws the panel (`sideBySide`, `topAndBottom`) is the member's own choice and
// is kept. The rule has two readers, one per direction of layout state:
//
//   - LOAD — `unifiedRouter`'s decode and apply seam (`validateState`,
//     `applyWorkspaceState`). A `?state=` tree that names
//     `secondaryPanel.horizontalTab` and no `workspace.layout` (a We message
//     link, the signed-in landing, a notification deep link, a preset) opens
//     with the layout `layoutShowingPanel` answers, and `WorkspaceShell`'s
//     restore watcher applies it.
//   - IN SESSION — a navigator the member (or Artemis) drives while the
//     workspace is live calls `surfaceSecondaryPanel` before it moves the tab.
//
// This module imports NOTHING. `unifiedRouter.js` is one of its readers, and
// the router's acyclic-import invariant means anything it reaches must not
// lead back to a store (the `utils/panelKeys.js` rule).

/** The three workspace layouts, in the order the layout control offers them. */
export const WORKSPACE_LAYOUTS = Object.freeze(["sideBySide", "topAndBottom", "fullScreen"]);

/** The one layout that draws no secondary panel. */
export const PANEL_HIDDEN_LAYOUT = "fullScreen";

/** The layout a hidden panel surfaces into. */
export const PANEL_SURFACING_LAYOUT = "sideBySide";

/**
 * The smallest viewport height (px, `window.innerHeight`) at which each layout
 * is still usable, and the ONE place that cutoff lives (th-5n61wn).
 *
 * `topAndBottom` stacks the map over the SecondaryPanel under the 64px (`h-16`)
 * header with a 10px gutter between them, so 720px leaves each half about
 * 320px: (720 - 64 - 10) / 2 = 323. Below that both halves are slivers.
 *
 * A layout absent from this map fits every viewport the desktop shell renders
 * in. `fullScreen` draws one pane. `sideBySide` splits the WIDTH, and the
 * shell's 1024px minimum already exceeds its 450px panel plus a usable map; it
 * is also `PANEL_SURFACING_LAYOUT`, the layout a hidden panel surfaces into, so
 * it must always be available.
 */
export const LAYOUT_MIN_VIEWPORT_HEIGHT = Object.freeze({ topAndBottom: 720 });

/**
 * Whether `layout` is usable in a viewport `height` px tall.
 *
 * @param {string} layout
 * @param {{ height: number }} viewport
 * @returns {boolean}
 */
export function layoutFitsViewport(layout, { height }) {
  const minHeight = LAYOUT_MIN_VIEWPORT_HEIGHT[layout];
  return minHeight === undefined || height >= minHeight;
}

/**
 * The layouts the layout control offers in `viewport`, in roster order: every
 * layout that fits, PLUS `activeLayout` whether it fits or not.
 *
 * The active layout is never hidden (founder ruling, th-5n61wn). A member who
 * shrinks the window, or opens a `?state=` link or a changeset-review restore
 * that names a layout this viewport cannot fit, keeps the button that shows
 * where they are; it goes once they switch away. Nothing here moves the layout,
 * so resizing back up loses nothing.
 *
 * @param {string|null} activeLayout
 * @param {{ height: number }} viewport
 * @returns {string[]}
 */
export function offeredLayouts(activeLayout, viewport) {
  return WORKSPACE_LAYOUTS.filter(
    (layout) => layout === activeLayout || layoutFitsViewport(layout, viewport),
  );
}

/**
 * The layout that draws the secondary panel, starting from `layout`:
 * `fullScreen` surfaces into `sideBySide`, and any other value is returned
 * unchanged.
 *
 * @param {string|null} layout
 * @returns {string|null}
 */
export function layoutShowingPanel(layout) {
  return layout === PANEL_HIDDEN_LAYOUT ? PANEL_SURFACING_LAYOUT : layout;
}

/**
 * The in-session half of the rule: surface the secondary panel through the
 * layout store's own validated write, when the live layout hides it.
 *
 * It takes the store rather than resolving it, so each caller keeps the
 * resolution it already makes (a lazy one inside an action, or a dynamic
 * import), and it reads only `workspaceLayout` and `setLayout`, which every
 * layout-store double already answers.
 *
 * @param {{ workspaceLayout: string|null, setLayout: (layout: string) => void }} layoutStore
 * @returns {boolean} whether the layout moved
 */
export function surfaceSecondaryPanel(layoutStore) {
  const current = layoutStore.workspaceLayout;
  const next = layoutShowingPanel(current);
  if (next === current) return false;

  layoutStore.setLayout(next);
  return true;
}

/**
 * A layout hold: what the workspace looked like before a surface took the
 * panel, so it can be put back exactly (the changeset review's takeover, and a
 * guided tour docking its reader from `fullScreen`; guided-tour-ux-polish
 * ST-R8). ONE shape for every holder:
 *   - workspaceLayout: the layout, which may still be null before it resolved;
 *   - sideBySideWidthOption: the width option's label;
 *   - gridTemplateColumns: the member's ACTUAL columns, which a gutter drag can
 *     put somewhere no width option names. Restoring the option alone would
 *     answer "put back exactly what the member had" with a stock 450px.
 *
 * @param {{ workspaceLayout: string|null, sideBySideWidthOption: string, savedGridTemplateColumns: string|null }} layoutStore
 * @returns {{ workspaceLayout: string|null, sideBySideWidthOption: string, gridTemplateColumns: string|null }}
 */
export function captureLayoutHold(layoutStore) {
  return {
    workspaceLayout: layoutStore.workspaceLayout,
    sideBySideWidthOption: layoutStore.sideBySideWidthOption,
    gridTemplateColumns: layoutStore.savedGridTemplateColumns,
  };
}

/**
 * Put back exactly what a layout hold recorded (`captureLayoutHold`).
 *
 * `setLayout` silently ignores anything outside the roster, so a hold taken
 * before the layout resolved (null) would restore nothing and strand the
 * member at the holder's width. The width is restored regardless; only the
 * layout itself is conditional. The member's exact columns win over the
 * option's own geometry, which would round a gutter drag off; a hold that
 * never captured columns (taken before the shell had laid out) falls back to
 * applying the option.
 *
 * @param {object} layoutStore - the workspace layout store
 * @param {{ workspaceLayout: string|null, sideBySideWidthOption: string, gridTemplateColumns: string|null }} hold
 */
export function restoreLayoutHold(layoutStore, hold) {
  if (WORKSPACE_LAYOUTS.includes(hold.workspaceLayout)) {
    layoutStore.setLayout(hold.workspaceLayout);
  }
  if (hold.gridTemplateColumns) {
    layoutStore.setSideBySideWidthOption(hold.sideBySideWidthOption);
    layoutStore.applyGridTemplateColumns(hold.gridTemplateColumns);
  } else {
    layoutStore.applySideBySideWidthOption(hold.sideBySideWidthOption);
  }
}
